import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ChevronUp, ChevronDown, Menu, X } from "lucide-react";
import { SLIDES_META, WHATSAPP_URL } from "@/data/constants";
import { SLIDES } from "@/components/slides";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Presentation() {
  const deckRef = useRef(null);
  const [active, setActive] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const { scrollYProgress } = useScroll({ container: deckRef });
  const progressX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 22,
    restDelta: 0.001,
  });

  const getSlides = () =>
    deckRef.current ? Array.from(deckRef.current.querySelectorAll("section.slide")) : [];

  // Intersection observer to track active slide
  useEffect(() => {
    const root = deckRef.current;
    if (!root) return;
    const slides = getSlides();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            const idx = slides.indexOf(entry.target);
            if (idx !== -1) setActive(idx);
          }
        });
      },
      { root, threshold: [0.55, 0.75] },
    );
    slides.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goTo = (idx) => {
    const slides = getSlides();
    const target = slides[idx];
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goTo(Math.min(active + 1, SLIDES_META.length - 1));
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goTo(Math.max(active - 1, 0));
      } else if (e.key === "Home") {
        goTo(0);
      } else if (e.key === "End") {
        goTo(SLIDES_META.length - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <div className="font-body text-zinc-950 bg-white">
      {/* Top progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] origin-left z-50 bg-orange-500"
        style={{ scaleX: progressX }}
        data-testid="progress-bar"
      />

      {/* Hamburger menu trigger */}
      <button
        type="button"
        aria-label="Abrir índice de diapositivas"
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="menu-toggle"
        className="fixed top-4 right-4 md:top-6 md:right-6 z-[60] grid place-items-center w-11 h-11 rounded-full bg-zinc-950 text-white hover:bg-orange-500 transition-colors shadow-lg"
      >
        {menuOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Slide-out index menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[55] w-[300px] sm:w-[340px] bg-zinc-950 text-white p-8 pt-20 overflow-y-auto"
            data-testid="menu-panel"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-6">
              Índice
            </div>
            <nav className="flex flex-col gap-1">
              {SLIDES_META.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => goTo(i)}
                  data-testid={`menu-item-${i}`}
                  className={`group flex items-center justify-between text-left py-2.5 border-b border-white/5 transition-colors ${
                    active === i ? "text-orange-400" : "text-white/80 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-white/40 w-5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-medium text-sm">{s.label}</span>
                  </span>
                  <span
                    className={`h-px transition-all ${
                      active === i ? "w-8 bg-orange-500" : "w-3 bg-white/20 group-hover:w-6 group-hover:bg-white/60"
                    }`}
                  />
                </button>
              ))}
            </nav>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Lateral progress dots */}
      <div
        className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col gap-3"
        data-testid="nav-dots"
      >
        {SLIDES_META.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Ir a diapositiva ${i + 1}: ${s.label}`}
            data-testid={`nav-dot-${i}`}
            className="group relative grid place-items-center w-5 h-5"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                active === i
                  ? "w-3 h-3 bg-orange-500"
                  : "w-2 h-2 bg-zinc-400/60 group-hover:bg-zinc-700"
              }`}
            />
            <span
              className={`absolute right-7 whitespace-nowrap text-[11px] uppercase tracking-[0.18em] font-semibold transition-opacity ${
                active === i ? "opacity-100 text-zinc-900" : "opacity-0 group-hover:opacity-70 text-zinc-700"
              }`}
            >
              {s.label}
            </span>
          </button>
        ))}
      </div>

      {/* Up/Down arrows */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
        <button
          type="button"
          aria-label="Diapositiva anterior"
          disabled={active === 0}
          onClick={() => goTo(active - 1)}
          data-testid="nav-arrow-up"
          className="grid place-items-center w-11 h-11 rounded-full bg-white text-zinc-950 ring-1 ring-zinc-200 hover:bg-zinc-950 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          <ChevronUp size={18} />
        </button>
        <button
          type="button"
          aria-label="Siguiente diapositiva"
          disabled={active === SLIDES_META.length - 1}
          onClick={() => goTo(active + 1)}
          data-testid="nav-arrow-down"
          className="grid place-items-center w-11 h-11 rounded-full bg-zinc-950 text-white hover:bg-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-md"
        >
          <ChevronDown size={18} />
        </button>
      </div>

      {/* Floating WhatsApp button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactar por WhatsApp"
        data-testid="whatsapp-float"
        className="fixed bottom-6 left-6 z-40 group"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] blur-md opacity-60 group-hover:opacity-90 transition-opacity" />
        <span className="relative grid place-items-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-xl ring-2 ring-white/40 group-hover:scale-110 transition-transform">
          <WhatsAppIcon className="w-6 h-6" />
        </span>
        <span className="pointer-events-none absolute left-16 top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-md bg-zinc-950 text-white text-[11px] font-semibold uppercase tracking-[0.18em] opacity-0 group-hover:opacity-100 transition-opacity">
          Hablemos por WhatsApp
        </span>
      </a>

      {/* Deck */}
      <main ref={deckRef} className="deck" data-testid="deck">
        {SLIDES_META.map((s, i) => {
          const Comp = SLIDES[s.key];
          return (
            <Comp
              key={s.id}
              id={s.id}
              index={i}
              total={SLIDES_META.length}
              goTo={goTo}
            />
          );
        })}
      </main>
    </div>
  );
}
